// Reads the client-migration workbook into the upload payload, and writes the
// "processed" copy: the original file plus what the importer worked out (due
// dates, AUM in crores, CRM client codes) and a status/notes column per row.
import * as XLSX from 'xlsx';

import type {
  MigrationCell,
  MigrationClientResult,
  MigrationIssue,
  MigrationReport,
  MigrationSheetName,
  MigrationSheetRow,
  MigrationSheets,
} from '@/services/migration.service';

// SYNC: headerKey in server/src/services/migration/migrationCells.ts.
const headerKey = (header: unknown): string =>
  String(header ?? '')
    .split('(')[0]
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();

interface SheetDef {
  key: keyof MigrationSheets;
  label: MigrationSheetName;
  /** A header only this sheet's header row has, used to find that row. */
  anchor: string;
}

const SHEET_DEFS: SheetDef[] = [
  { key: 'rms', label: 'RMs', anchor: 'rmcode' },
  { key: 'clients', label: 'Clients', anchor: 'clientcode' },
  { key: 'orders', label: 'Orders', anchor: 'activatedon' },
  { key: 'additionalArns', label: 'AdditionalARNs', anchor: 'parentclientcode' },
  { key: 'subscriptions', label: 'LiveSubscriptions', anchor: 'productcode' },
];

// The template's reference copy carries two annotation rows above the headers,
// the plain copy none - so the header row is found, not assumed.
const HEADER_SEARCH_ROWS = 6;

const findSheet = (wb: XLSX.WorkBook, label: string): XLSX.WorkSheet | null => {
  const name = wb.SheetNames.find((n) => headerKey(n) === headerKey(label));
  return name ? (wb.Sheets[name] ?? null) : null;
};

const rowsOf = (ws: XLSX.WorkSheet): unknown[][] =>
  XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: null, raw: true });

const headerRowIndex = (rows: unknown[][], anchor: string): number =>
  rows.slice(0, HEADER_SEARCH_ROWS).findIndex((r) => r.some((c) => headerKey(c) === anchor));

const toCell = (v: unknown): MigrationCell => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v;
  return String(v);
};

export interface ParsedMigrationWorkbook {
  sheets: MigrationSheets;
  buffer: ArrayBuffer;
}

export const parseMigrationWorkbook = async (file: File): Promise<ParsedMigrationWorkbook> => {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array' });
  const sheets: MigrationSheets = {
    rms: null,
    clients: null,
    orders: null,
    additionalArns: null,
    subscriptions: null,
  };
  SHEET_DEFS.forEach((def) => {
    const ws = findSheet(wb, def.label);
    if (!ws) return;
    const rows = rowsOf(ws);
    const h = headerRowIndex(rows, def.anchor);
    if (h < 0) {
      sheets[def.key] = [];
      return;
    }
    const header = rows[h];
    const parsed: MigrationSheetRow[] = rows
      .slice(h + 1)
      .map((r, i) => ({
        sheetRow: h + 2 + i,
        cells: Object.fromEntries(
          header
            .map((col, j): [string, MigrationCell] => [String(col ?? '').trim(), toCell(r[j])])
            .filter(([col]) => col !== ''),
        ),
      }))
      .filter((r) => Object.values(r.cells).some((v) => v !== null && String(v).trim() !== ''));
    sheets[def.key] = parsed;
  });
  return { sheets, buffer };
};

// ─── Processed workbook ──────────────────────────────────────────────────────
const statusLabel = (report: MigrationReport, client: MigrationClientResult | undefined): string => {
  if (!client) return 'Not linked to a client';
  if (client.outcome === 'existing') return 'Already in CRM';
  if (client.outcome === 'skip') return 'Skipped';
  return report.mode === 'commit' ? 'Imported' : 'Ready to import';
};

const notesFor = (issues: MigrationIssue[]): string =>
  issues.map((i) => `${i.level === 'error' ? 'ERROR' : 'Warning'}: ${i.message}`).join(' | ');

/** Writes `values` into the header row's columns (appending unknown headers). */
const writeRow = (
  ws: XLSX.WorkSheet,
  headerRow: number,
  columns: Map<string, number>,
  sheetRow: number,
  values: Record<string, string | number | null>,
): void => {
  Object.entries(values).forEach(([header, value]) => {
    let col = columns.get(headerKey(header));
    if (col === undefined) {
      col = Math.max(-1, ...columns.values()) + 1;
      columns.set(headerKey(header), col);
      XLSX.utils.sheet_add_aoa(ws, [[header]], { origin: { r: headerRow, c: col } });
    }
    if (value === null) return;
    XLSX.utils.sheet_add_aoa(ws, [[value]], { origin: { r: sheetRow - 1, c: col } });
  });
};

const columnsOf = (headerRow: unknown[]): Map<string, number> =>
  new Map(headerRow.map((h, i) => [headerKey(h), i]).filter(([k]) => k !== '') as [string, number][]);

const STATUS = 'Migration Status';
const NOTES = 'Migration Notes';

/** The original workbook annotated with the report; downloaded as .xlsx. */
export const downloadProcessedWorkbook = (
  buffer: ArrayBuffer,
  report: MigrationReport,
  fileName: string,
): void => {
  const wb = XLSX.read(buffer, { type: 'array' });
  const byCode = new Map(report.clients.map((c) => [c.clientCode, c]));
  const clientOfRow = (sheet: MigrationSheetName, row: number): MigrationClientResult | undefined =>
    report.clients.find((c) =>
      sheet === 'Clients'
        ? c.sheetRows.includes(row)
        : c.subscriptions.some((s) => s.sheetRow === row && sheet === 'LiveSubscriptions') ||
          c.issues.some((i) => i.sheet === sheet && i.sheetRow === row),
    );
  const rowIssues = (sheet: MigrationSheetName, row: number): MigrationIssue[] => [
    ...report.clients.flatMap((c) => c.issues),
    ...report.unlinkedIssues,
  ].filter((i) => i.sheet === sheet && i.sheetRow === row);

  SHEET_DEFS.forEach((def) => {
    const ws = findSheet(wb, def.label);
    if (!ws) return;
    const rows = rowsOf(ws);
    const h = headerRowIndex(rows, def.anchor);
    if (h < 0) return;
    const columns = columnsOf(rows[h]);
    rows.slice(h + 1).forEach((r, i) => {
      if (!r.some((c) => c !== null && String(c).trim() !== '')) return;
      const sheetRow = h + 2 + i;
      const code = String(r[columns.get('clientcode') ?? -1] ?? r[columns.get('parentclientcode') ?? -1] ?? '').trim();

      if (def.key === 'rms') {
        const rmCode = String(r[columns.get('rmcode') ?? -1] ?? '').trim().toUpperCase();
        const rm = report.rms.find((x) => x.rmCode === rmCode);
        const status = { existing: 'Already in CRM', create: report.mode === 'commit' ? 'Created - awaiting approval' : 'Will be created (pending approval)', rejected: 'Not added' };
        writeRow(ws, h, columns, sheetRow, {
          [STATUS]: rm ? status[rm.outcome] : '',
          [NOTES]: rm ? notesFor(rm.issues) : '',
        });
        return;
      }

      const client =
        def.key === 'clients'
          ? clientOfRow('Clients', sheetRow)
          : (byCode.get(code) ?? clientOfRow(def.label, sheetRow));
      const values: Record<string, string | number | null> = {
        [STATUS]: statusLabel(report, client),
        [NOTES]: notesFor(def.key === 'clients' ? (client?.issues.filter((x) => x.sheet === 'Clients') ?? []) : rowIssues(def.label, sheetRow)),
      };

      if (def.key === 'clients' && client) {
        values['CRM Client Code'] = client.crmClientCode;
        values.Email = client.normalised.email;
        values.SecondaryEmail = client.normalised.secondaryEmail;
        values.ARNAum = client.normalised.arnAumCrores;
      }
      if (def.key === 'additionalArns' && client) {
        values.AUM = client.normalised.additionalArnAum[String(sheetRow)] ?? null;
      }
      if (def.key === 'subscriptions' && client) {
        const sub = client.subscriptions.find((s) => s.sheetRow === sheetRow);
        if (sub) {
          values['NextDueDate (YYYY-MM-DD)'] = sub.nextDueDate;
          values['Computed Annual Renewal Amount'] = sub.annualAmount;
          values['Computed Amount Per Cycle'] = sub.perCycleAmount;
          values['Amount Basis'] = sub.amountBasis;
          values['Computed CycleNumber'] = sub.cycleNumber;
        }
      }
      writeRow(ws, h, columns, sheetRow, values);
    });
  });
  XLSX.writeFile(wb, fileName);
};
